import PropTypes from 'prop-types';
import { useEffect } from 'react';

import { selectUserRolePermissions } from '@socialive/common';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { SUPPORT_LINK } from '~/helpers/constants';
import AppEnvSelector from './AppEnvSelector';
import BroadcastItem, { NoBroadcastsView } from './Broadcast';

import { Pathnames } from '../helpers/enums';
import { broadcastsDataShape } from '../helpers/propTypeShapes';

import '../style/broadcasts.css';

export default function Broadcasts({
  accountId,
  APIBaseURL,
  broadcastsData,
  handleStudioURLChange,
  loadBroadcasts,
  menuEnv,
  reAuth,
  trackEvent,
}) {
  const { canNavigateInPortal } = useSelector(selectUserRolePermissions);
  const { pathname } = useLocation();

  let greetingHeader = canNavigateInPortal ? "You've been invited to a broadcast" : 'Join a Broadcast';
  if (canNavigateInPortal && pathname === Pathnames.DASHBOARD) {
    greetingHeader = 'My Broadcasts';
  }

  const greetingSubtitle =
    canNavigateInPortal && pathname === Pathnames.DASHBOARD
      ? 'enter Studio as the broadcast host'
      : 'join as a guest presenter';

  useEffect(() => {
    loadBroadcasts();
  }, [loadBroadcasts, pathname, accountId]);

  if (broadcastsData.loading) {
    return <div />;
  }

  if (broadcastsData.error) {
    return <div className="broadcasts-container broadcasts-container-centered">Uh oh! Something went wrong.</div>;
  }

  if (broadcastsData.noBroadcasts) {
    return <NoBroadcastsView loadBroadcasts={loadBroadcasts} reAuth={reAuth} trackEvent={trackEvent} />;
  }

  return (
    <div className="broadcasts-container">
      <div className="sandbox-selector">
        <AppEnvSelector handleStudioURLChange={handleStudioURLChange} menuEnv={menuEnv} />
      </div>
      <div>
        <h2 id="qaidBroadcastsGreeting" className="broadcasts-greeting">
          {greetingHeader}
        </h2>
        <div id="qaidBroadcastsGreetingSubtitle" className="broadcasts-greeting-subtitle">
          Click on a broadcast below to {greetingSubtitle}. Make sure you have a stable internet connection, ideally
          connected via ethernet. It&apos;s best to have front-facing lighting and a camera at eye-level.{' '}
          <a
            id="qaidBroadcastsTipLink"
            className="broadcasts-tip-link"
            href={SUPPORT_LINK.GREEN_ROOM_EXPERIENCE}
            target="_blank"
            rel="noopener noreferrer"
          >
            Click here
          </a>{' '}
          for more tips.
        </div>
      </div>
      {broadcastsData.list.map((broadcast) => (
        <BroadcastItem
          key={broadcast.id}
          APIBaseURL={APIBaseURL}
          broadcast={broadcast}
          reloadBroadcasts={loadBroadcasts}
          trackEvent={trackEvent}
        />
      ))}
      <div id="qaidBroadastsFootnote" className="broadcasts-footnote">
        {canNavigateInPortal && pathname === Pathnames.DASHBOARD
          ? 'You can enter Studio from your desktop using an updated version of Chrome or Edge.'
          : 'Join from your desktop using an updated version of Chrome or Edge, or from your iOS device using the Socialive mobile app.'}
      </div>
    </div>
  );
}

Broadcasts.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  APIBaseURL: PropTypes.string.isRequired,
  broadcastsData: broadcastsDataShape.isRequired,
  handleStudioURLChange: PropTypes.func.isRequired,
  loadBroadcasts: PropTypes.func.isRequired,
  menuEnv: PropTypes.string.isRequired,
  reAuth: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
};
